/* eslint-disable import/no-webpack-loader-syntax */
import * as duckdb from "@duckdb/duckdb-wasm";
import duckdbWasm from "!!file-loader?name=duckdb-mvp-[contenthash].wasm!@duckdb/duckdb-wasm/dist/duckdb-mvp.wasm";
import duckdbWasmEh from "!!file-loader?name=duckdb-eh-[contenthash].wasm!@duckdb/duckdb-wasm/dist/duckdb-eh.wasm";
import mvpWorker from "!!file-loader?name=duckdb-browser-mvp.worker-[contenthash].js!@duckdb/duckdb-wasm/dist/duckdb-browser-mvp.worker.js";
import ehWorker from "!!file-loader?name=duckdb-browser-eh.worker-[contenthash].js!@duckdb/duckdb-wasm/dist/duckdb-browser-eh.worker.js";
import initSqlJs from "sql.js";
import pako from "pako";
import sqlWasm from "!!file-loader?name=sql-wasm-[contenthash].wasm!sql.js/dist/sql-wasm.wasm";

/**
 * Create views in DuckDB using pre-signed SAS URLs for Parquet files
 * This allows querying authenticated Azure blob storage files
 *
 * @param {duckdb.AsyncDuckDBConnection} conn - DuckDB connection
 * @param {Object} parquetFiles - Object mapping table names to SAS URLs
 * @returns {Promise<void>}
 */
async function createParquetViews(conn, parquetFiles) {
  const entries = Object.entries(parquetFiles);
  await Promise.all(
    entries.map(async ([tableName, sasUrl]) => {
      await conn.query(`CREATE VIEW ${tableName} AS SELECT * FROM '${sasUrl}'`);
    })
  );
}

/**
 * Convert DuckDB query result to sql.js compatible format
 * @param {any} result - DuckDB query result
 * @returns {Array} Array with columns and values in sql.js format
 */
function duckdbToSqlJsFormat(result) {
  if (!result || result.numRows === 0) {
    return [];
  }

  const columns = result.schema.fields.map((field) => field.name);
  const values = [];

  for (let i = 0; i < result.numRows; i += 1) {
    const row = [];
    for (let j = 0; j < columns.length; j += 1) {
      const col = columns[j];
      const val = result.getChild(col).get(i);
      if (typeof val === "bigint") {
        row.push(Number(val));
      } else {
        row.push(val);
      }
    }
    values.push(row);
  }

  return [{ columns, values }];
}

/**
 * Wrapper around DuckDB connection that mimics sql.js API
 */
class DuckDBSqlJsCompat {
  constructor(conn) {
    this.conn = conn;
  }

  /**
   * Execute SQL query and return results in sql.js format
   * @param {string} sql - SQL query
   * @returns {Array} Results in sql.js format: [{ columns, values }]
   */
  async exec(sql) {
    const result = await this.conn.query(sql);
    return duckdbToSqlJsFormat(result);
  }

  /**
   * Close the connection
   */
  async close() {
    await this.conn.close();
  }
}

async function initDuckDB(parquetFiles) {
  const MANUAL_BUNDLES = {
    mvp: {
      mainModule: duckdbWasm,
      mainWorker: mvpWorker,
    },
    eh: {
      mainModule: duckdbWasmEh,
      mainWorker: ehWorker,
    },
  };

  // Select a bundle based on browser capabilities
  const bundle = await duckdb.selectBundle(MANUAL_BUNDLES);

  const worker = new Worker(bundle.mainWorker);
  const logger = new duckdb.ConsoleLogger();
  const db = new duckdb.AsyncDuckDB(logger, worker);
  await db.instantiate(bundle.mainModule, bundle.pthreadWorker);

  const conn = await db.connect();
  await createParquetViews(conn, parquetFiles);
  const sqliteApi = new DuckDBSqlJsCompat(conn);
  return sqliteApi;
}

async function initSQLite(sqlite) {
  const SQL = await initSqlJs({ locateFile: () => sqlWasm });
  const db = new SQL.Database(Buffer.from(sqlite.data || sqlite));
  return db;
}

async function fetchAndInitSQLite(url) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch SQLite database from ${url}: ${response.statusText}`);
  }
  const arrayBuffer = await response.arrayBuffer();

  // Decompress the gzipped database file
  const compressed = new Uint8Array(arrayBuffer);
  const decompressed = pako.ungzip(compressed);

  // Reuse initSQLite with the decompressed data
  return initSQLite(decompressed);
}

export default async function initSqlDb(output) {
  if (output.parquet_files) {
    return initDuckDB(output.parquet_files);
  }
  if (output.output_sql) {
    return initSQLite(output.output_sql);
  }
  if (output.output_url) {
    return fetchAndInitSQLite(output.output_url);
  }
  throw new Error("couldn't find a way to load outputs");
}
